import { http } from '@/utils/api'
import { directorToken, isProduction } from '@/utils/environment'
import * as Sentry from '@/core/sentry'
import { isWhitelistError } from '@/utils/api/whitelist'
import type { WhoamiResponse } from '@/types/backend/whoamiResponse'
import { isKeycloakUsed, refreshKeycloakToken } from '@/core/keycloak'
import { isMaintenanceError, isNotFoundError } from '@/utils/errors'

export type LoginResponse = {
  access_token: string
  token_type: string
  expires_in: number
}

export async function loadToken(username: string, password?: string) {
  try {
    const { data } = await http.post<LoginResponse>(
      '/login',
      { username, password },
      {
        headers: {
          'auth-token': directorToken() ?? '',
        },
      }
    )

    return {
      status: 'success' as const,
      payload: data,
    }
  } catch (error) {
    if (!isWhitelistError(error)) {
      Sentry.captureException(error)
    }

    return {
      status: 'error' as const,
      payload: error,
    }
  }
}

export async function loadWhoami() {
  try {
    const { data } = await http.get<WhoamiResponse>('/whoami')

    return {
      status: 'success' as const,
      payload: data,
    }
  } catch (error) {
    Sentry.captureException(error)

    if (isMaintenanceError(error)) {
      return {
        status: 'error' as const,
        type: 'maintenance' as const,
        payload: error,
      }
    }

    if (isNotFoundError(error)) {
      return {
        status: 'error' as const,
        type: 'not-found' as const,
        payload: error,
      }
    }

    return {
      status: 'error' as const,
      type: 'other' as const,
      payload: error,
    }
  }
}

export async function loadRefreshJwtToken() {
  try {
    const { data } = await http.post<LoginResponse>('/refresh', {})

    return {
      status: 'success' as const,
      payload: data.access_token,
    }
  } catch (error) {
    if (!isWhitelistError(error)) {
      Sentry.captureException(error)
    }

    return {
      status: 'error' as const,
      payload: error,
    }
  }
}

export async function loadRefreshKeycloakToken() {
  try {
    const token = await refreshKeycloakToken()

    if (!token) {
      throw new Error('Keycloak token not available')
    }

    return {
      status: 'success' as const,
      payload: token,
    }
  } catch (error) {
    // DEBUG: remove when we know it's working
    if (!isProduction()) {
      // eslint-disable-next-line no-console
      console.error(error)
    }

    Sentry.captureException(error)

    return {
      status: 'error' as const,
      payload: error,
    }
  }
}

export async function loadRefreshToken() {
  if (isKeycloakUsed()) {
    return await loadRefreshKeycloakToken()
  } else {
    return await loadRefreshJwtToken()
  }
}
